import { useMemo } from 'react';

import {
	type ConfluenceRangeFilter,
	type EntityATI,
	type SearchJiraProductType,
	type ThirdPartyFilters,
} from '@atlassian/dynamic-cards-search-client';

import { CUSTOM_OPTION_VALUE, type TypeFilterValueKey } from '../../../common/constants/filters';
import { ProductKeys } from '../../../common/constants/products';
import { type SelectedFiltersType } from '../../../common/constants/types';
import { type OutboundAuthConfigsType } from '../../../common/types';
import {
	baseIntegrationARIs,
	convertCustomDateToRange,
	convertLastUpdatedToDateRange,
	getConfluenceDateOptionFromCustomDates,
	getConfluenceDateOptionValues,
	getThirdPartyFilters,
} from '../../../common/utils/filters';
import { useFilters, useProducts, useTypeEntities } from '../../store/search';
import {
	assigneeFilter,
	contentFilter,
	contributorFilter,
	labelFilter,
	lastUpdatedFilter,
	ownerFilter,
	projectFilter,
	spaceFilter,
	titleMatchFilter,
	verifiedFilter,
} from '../../store/search/filters';
import { isSelectFilter } from '../base-select-filter';
import { type FilterStates } from '../types';

const getSelectFilterValue = (filter?: FilterStates) => {
	return isSelectFilter(filter?.type) ? filter.value : [];
};

const getSelectFilterCustomOptionValue = (filter?: FilterStates) => {
	return isSelectFilter(filter?.type) ? filter.customValue : {};
};

const getBooleanFilterValue = (filter?: FilterStates) => {
	return filter?.type === 'boolean' ? filter.value : false;
};

const getSelectedDateRange = (filter?: FilterStates) => {
	const selectedDateOption = getSelectFilterValue(filter).at(0);
	if (selectedDateOption && selectedDateOption !== CUSTOM_OPTION_VALUE) {
		return convertLastUpdatedToDateRange(selectedDateOption);
	} else if (selectedDateOption === CUSTOM_OPTION_VALUE) {
		const customDateRange = getSelectFilterCustomOptionValue(filter);
		if (customDateRange?.from || customDateRange?.to) {
			return convertCustomDateToRange(customDateRange.from, customDateRange.to);
		}
	}
	return;
};

const getFilterDateRange = (filter?: FilterStates): ConfluenceRangeFilter[] => {
	const selectedDateOption = getSelectFilterValue(filter).at(0);

	if (selectedDateOption && selectedDateOption !== CUSTOM_OPTION_VALUE) {
		return [getConfluenceDateOptionValues(selectedDateOption)];
	} else if (selectedDateOption === CUSTOM_OPTION_VALUE) {
		const customDateRange = getSelectFilterCustomOptionValue(filter);
		if (customDateRange?.from && customDateRange?.to) {
			return [getConfluenceDateOptionFromCustomDates(customDateRange.from, customDateRange.to)];
		}
	}
	return [];
};

export const mapJiraProductToProjectFilter = (
	selectedProduct?: ProductKeys,
): SearchJiraProductType[] => {
	switch (selectedProduct) {
		case ProductKeys.Jira:
			return ['software', 'business'];
		case ProductKeys.JiraProductDiscovery:
			return ['product_discovery'];
		case ProductKeys.JiraServiceManagement:
			return ['service_desk'];
	}

	return ['software', 'business', 'service_desk', 'product_discovery'];
};

export const useSelectedFilters = ({
	outboundAuthConfigs,
}: {
	outboundAuthConfigs: OutboundAuthConfigsType;
}): {
	selectedFilters: SelectedFiltersType;
	unselectedProductsThirdPartyFilters: ThirdPartyFilters;
	hasFiltersSelected: boolean;
	selectedEntities: string[];
	entitiesForResultsCountQuery: EntityATI[];
	entities: EntityATI[];
} => {
	const filters = useFilters();
	const { selectedProduct, filteredProducts, availableProducts } = useProducts();
	const { selectedEntities, entitiesForQuery, entitiesForResultsCountQuery } = useTypeEntities({
		outboundAuthConfigs,
	});

	const titleMatchValue = getBooleanFilterValue(filters[titleMatchFilter.id]);
	const selectedDateRange = getSelectedDateRange(filters[lastUpdatedFilter.id]);
	const filterDateRange = getFilterDateRange(filters[lastUpdatedFilter.id]);
	// fg('universal-contributor-filter') => false
	const commonContributors = false
		? getSelectFilterValue(filters[contributorFilter.id]).length
			? getSelectFilterValue(filters[contributorFilter.id])
			: undefined
		: undefined;
	const selectedFilters = useMemo(
		() =>
			({
				commonFilters: {
					range: {
						lastModified: selectedDateRange,
					},
					contributorsFilter: commonContributors,
				},
				jiraFilters: {
					issueFilter: {
						projectARIs: getSelectFilterValue(filters[projectFilter.id]),
						assigneeARIs: getSelectFilterValue(filters[assigneeFilter.id]),
					},
					projectFilter: {
						// fg('enable_1p_search_jpd_jsm') => false
						projectTypes: false ? mapJiraProductToProjectFilter(selectedProduct) : undefined,
					},
				},
				confluenceFilters: {
					spacesFilter: getSelectFilterValue(filters[spaceFilter.id]),
					isVerified: getBooleanFilterValue(filters[verifiedFilter.id]) || undefined,
					contributorsFilter: getSelectFilterValue(filters[contributorFilter.id]),
					ancestorIdsFilter: getSelectFilterValue(filters[contentFilter.id]),
					range: filterDateRange,
					owners: getSelectFilterValue(filters[ownerFilter.id]),
					labelsFilter: getSelectFilterValue(filters[labelFilter.id]),
				},
				thirdPartyFilters: getThirdPartyFilters(
					filteredProducts,
					titleMatchValue,
					selectedEntities as TypeFilterValueKey[],
					outboundAuthConfigs,
					baseIntegrationARIs,
					filterDateRange,
				),
			}) satisfies SelectedFiltersType,
		[
			filterDateRange,
			filteredProducts,
			filters,
			outboundAuthConfigs,
			selectedDateRange,
			selectedEntities,
			selectedProduct,
			titleMatchValue,
			commonContributors,
		],
	);

	const unselectedProductsThirdPartyFilters = useMemo(
		() =>
			getThirdPartyFilters(
				availableProducts,
				titleMatchValue,
				selectedEntities as TypeFilterValueKey[],
				outboundAuthConfigs,
				baseIntegrationARIs,
				filterDateRange,
			),

		[availableProducts, titleMatchValue, selectedEntities, outboundAuthConfigs, filterDateRange],
	);

	const { commonFilters, jiraFilters, confluenceFilters, thirdPartyFilters } = selectedFilters;

	const isEmpty =
		!commonFilters.range.lastModified &&
		selectedEntities.length === 0 &&
		confluenceFilters.spacesFilter.length === 0 &&
		!confluenceFilters.isVerified &&
		confluenceFilters.contributorsFilter.length === 0 &&
		confluenceFilters.ancestorIdsFilter.length === 0 &&
		confluenceFilters.range.length === 0 &&
		confluenceFilters.owners.length === 0 &&
		confluenceFilters.labelsFilter.length === 0 &&
		jiraFilters.issueFilter.assigneeARIs.length === 0 &&
		jiraFilters.issueFilter.projectARIs.length === 0 &&
		(!thirdPartyFilters.range || thirdPartyFilters?.range?.length === 0) &&
		!thirdPartyFilters.titleMatchOnly &&
		thirdPartyFilters.subtypes.length === 0;

	return {
		selectedFilters,
		unselectedProductsThirdPartyFilters,
		selectedEntities,
		entitiesForResultsCountQuery,
		entities: entitiesForQuery,
		hasFiltersSelected: !isEmpty,
	};
};
